import { OnInit, OnDestroy } from '@angular/core';
import { Observable, EMPTY, of, fromEvent, timer, throwError, Subject, pipe, merge, Subscription } from 'rxjs';
import {
    switchMap,
    tap,
    shareReplay,
    filter,
    repeatWhen,
    catchError,
    retryWhen,
    delay,
    map,
    throttleTime,
    debounceTime,
} from 'rxjs/operators';
import { DateTime } from 'luxon';
import { extend } from 'lodash';
import * as Noty from 'noty';

import { Model } from '@app/models/model';
import { RequestError } from '@app/utils/envelope.utils';
import { AppService } from '@app/app.service';
import { UserService } from '@app/models/users/users.service';

export interface DataComponentOptions {
    // Time in milliseconds between data refreshes. 0 to disable, disabled by default.
    refresh: number;
    // Debounces manual reloads (with reload function) for this number of milliseconds.
    // Defaults to 100 milliseconds. Set to 0 to disable.
    debounceRefresh: number;
    // Whether a notification should be display when an error occurs. Defaults to true.
    // Turn this off if the component handles errors itself with onFailure() and this.error.
    displayErrors: boolean;
}

/**
 * This base component provides a few common features for components
 * that fetch and handle data, usually through services and an API.
 *
 * Data is accessed through the models$ and models/model properties. The template
 * should subscribe to the models$ observable with the async pipe to automatically
 * handle unsubscribing when the component is destroyed.
 *
 * It provides a loading property for templates, and it will automatically
 * update itself when data is being fetched.
 *
 * It provides a timed refresh capability, where data will be re-fetched
 * after a certain number of milliseconds. The refreshing will be paused
 * when the browser tab loses focus, and resumes when it is focused.
 */
export class DataComponent<T extends Model> implements OnInit, OnDestroy {
    loading = true;
    models$: Observable<T[] | T> = new Observable<T[] | T>(null);
    models: T[];
    model: T;
    error: RequestError;
    emitDestroy: Subject<boolean> = new Subject();

    private refresh = 0;
    private debounceRefresh = 100;
    private displayErrors = true;
    private reload$: Subject<boolean> = new Subject();
    private autoReload$ = new Subject<number>();
    private autoReloadTimer$: Subscription;
    private visibilityChange$ = fromEvent(document, 'visibilitychange').pipe(
        map(() => document.visibilityState === 'visible'),
        shareReplay({ refCount: true, bufferSize: 1 })
    );
    private fetchComplete = true;
    private lastFetch: DateTime;

    constructor(options?: Partial<DataComponentOptions>) {
        if (options) {
            this.refresh = options.refresh || 0;
            this.debounceRefresh = options.debounceRefresh || 100;
            this.displayErrors = !!options.displayErrors;
        }
        // Refresh shouldn't be faster than 1 second
        if (this.refresh && this.refresh < 1000) {
            this.refresh = 1000;
        }
        if (this.refresh) {
            this.autoReloadTimer$ = timer(this.refresh, this.refresh).subscribe((n) => this.autoReload$.next(n));
        }
    }

    ngOnInit() {
        setTimeout(() => {
            this.models$ = of(0).pipe(
                tap(() => {
                    this.fetchComplete = false;
                    this.lastFetch = DateTime.utc();
                    this.loading = true;
                }),
                switchMap(() => this.fetch()),
                // Repeat fetch for a variety of events
                repeatWhen((complete) =>
                    merge(
                        // Always reload manually
                        this.reload$.pipe(debounceTime(this.debounceRefresh)),
                        // Only reload on the timer when the request has finished and the page is visible
                        this.autoReload$.pipe(
                            filter(() => this.fetchComplete && document.visibilityState === 'visible')
                        ),
                        // When request completes, mark it as complete
                        complete.pipe(
                            tap(() => (this.fetchComplete = true)),
                            filter(() => false)
                        ),
                        // When page is visible and data refreshes, refetch if it hasn't been recently
                        this.visibilityChange$.pipe(
                            filter((vis) => {
                                if (
                                    !!vis &&
                                    this.refresh &&
                                    Math.abs(this.lastFetch.diffNow('milliseconds').as('milliseconds')) >= this.refresh
                                ) {
                                    return true;
                                }
                                return false;
                            })
                        )
                    )
                ),
                // Capture latest error in component, rethrow
                catchError((err) => {
                    this.error = err;
                    // If stream errors out right away, this will update the view before
                    // Angular has finished rendering the loading=true change, throwing
                    // an ExpressionChangedAfterItHasBeenCheckedError, so wait until the
                    // next event tick.
                    // Probably a better way to do this.
                    setTimeout(() => (this.loading = false));
                    this.onFailure();
                    if (this.displayErrors) {
                        new Noty({
                            type: 'error',
                            layout: 'topRight',
                            theme: 'nest',
                            text: `<h6 style="color: #fff">Error</h6><p style="color: #fff">${
                                this.error.message || this.error
                            }</p>`,
                            timeout: 10000,
                            closeWith: ['click', 'button'],
                        }).show();
                    }
                    return throwError(err);
                }),
                // On error, either keep retrying after delay without changing existing data (if any)
                // or wait for a manual refresh.
                retryWhen((errors) =>
                    merge(
                        this.reload$,
                        errors.pipe(
                            filter(() => !!this.refresh),
                            delay(this.refresh),
                            switchMap((err) => {
                                if (document.visibilityState === 'visible') {
                                    return of(err);
                                }
                                return this.visibilityChange$.pipe(filter((vis) => !!vis));
                            })
                        )
                    )
                ),
                // Request was successful, do stuff for that
                tap((data) => {
                    if (Array.isArray(data)) {
                        this.models = data;
                    } else {
                        this.model = data;
                    }
                    setTimeout(() => (this.loading = false));
                    this.onSuccess();
                })
            );
        });

        AppService.self.onReloadData().subscribe(() => this.reload$.next(true));
        this.onInit();
    }

    ngOnDestroy() {
        this.emitDestroy.next(true);
        this.emitDestroy.complete();
        this.onDestroy();
    }

    /**
     * The component overrides this, returning an observable that fetches
     * data and emits it.
     */
    fetch(): Observable<T[] | T> {
        return EMPTY;
    }

    /**
     * Executing this method will force a reload of the data,
     * ignoring any timers or errors.
     */
    reload() {
        this.reload$.next(true);
    }

    /**
     * Changes the auto refresh timer to the specified of milliseconds.
     * Will wait for that period before fetching for the first time.
     */
    setAutoReload(refresh: number) {
        if (refresh !== this.refresh) {
            this.refresh = refresh;
            if (this.autoReloadTimer$) {
                this.autoReloadTimer$.unsubscribe();
            }
            if (refresh) {
                this.autoReloadTimer$ = timer(this.refresh, this.refresh).subscribe((n) => this.autoReload$.next(n));
            }
        }
    }

    onInit(): void {}

    onDestroy(): void {}

    onSuccess(): void {}

    onFailure(): void {}
}
